import { useState, useCallback } from 'preact/hooks';
import type { ComponentChildren } from 'preact';

export interface Column<T> {
  header: string;
  key: string;
  render?: (row: T) => ComponentChildren;
  sortable?: boolean;
}

interface TableProps<T> {
  columns: Column<T>[];
  rows: T[];
  onRowClick?: (row: T) => void;
  className?: string;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function Table<T extends Record<string, any>>({
  columns,
  rows,
  onRowClick,
  className,
}: TableProps<T>) {
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const handleSort = useCallback((key: string) => {
    if (sortKey === key) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  }, [sortKey]);

  const sortedRows = sortKey
    ? [...rows].sort((a, b) => {
        const aVal = a[sortKey];
        const bVal = b[sortKey];
        if (aVal == null && bVal == null) return 0;
        if (aVal == null) return 1;
        if (bVal == null) return -1;
        const cmp = typeof aVal === 'number' && typeof bVal === 'number'
          ? aVal - bVal
          : String(aVal).localeCompare(String(bVal));
        return sortDir === 'asc' ? cmp : -cmp;
      })
    : rows;

  return (
    <table class={className}>
      <thead>
        <tr>
          {columns.map((col) => (
            <th
              key={col.key}
              class={`${col.sortable ? 'sortable' : ''} ${sortKey === col.key ? 'sorted' : ''}`}
              onClick={col.sortable ? () => handleSort(col.key) : undefined}
            >
              {col.header}
              {col.sortable && sortKey === col.key && (
                <span class="sort-indicator">
                  {sortDir === 'asc' ? '\u25B2' : '\u25BC'}
                </span>
              )}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {sortedRows.map((row, i) => (
          <tr
            key={i}
            class={onRowClick ? 'clickable' : undefined}
            onClick={onRowClick ? () => onRowClick(row) : undefined}
          >
            {columns.map((col) => (
              <td key={col.key}>
                {col.render ? col.render(row) : (row[col.key] as ComponentChildren)}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
